import { useState, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Pencil, Trash2, User, Mail, Phone, Building2 } from "lucide-react"
import { useContact, useDeleteContact } from "@/hooks/use-contacts"
import { RecordNavigation } from "@/components/record-navigation"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { EntityTabs, type TabConfig } from "@/components/entity-tabs"
import { QuickActions, type QuickActionsEntity } from "@/components/quick-actions"
import { formatDate } from "@/lib/utils"
import { toast } from "sonner"

const CONTACT_TABS: TabConfig[] = [
  { key: "notes", label: "Notatki", countKey: "notes" },
  { key: "activities", label: "Aktywnosci" },
  { key: "companies", label: "Firmy", countKey: "companies" },
  { key: "deals", label: "Transakcje", countKey: "deals" },
  { key: "tasks", label: "Zadania", countKey: "tasks" },
  { key: "service_tickets", label: "Serwis", countKey: "service_tickets" },
  { key: "documents", label: "Dokumenty", countKey: "documents" },
  { key: "quotes", label: "Oferty", countKey: "quotes" },
  { key: "invoices", label: "Faktury", countKey: "invoices" },
]

export function ContactDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useContact(id)
  const deleteMutation = useDeleteContact()
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined)
  const tabsRef = useRef<HTMLDivElement>(null)

  const contact = data?.data

  const handleOpenNotes = useCallback(() => {
    setActiveTab("notes")
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 50)
  }, [])

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab)
  }, [])

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac te osobe?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Kontakt usuniety")
    navigate("/contacts")
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!contact) {
    return <div className="text-muted-foreground">Nie znaleziono osoby</div>
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t("contacts.title"), href: "/contacts" },
          { label: contact.full_name },
        ]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/contacts")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <RecordNavigation entityType="contacts" currentId={id} basePath="/contacts" />
          <div>
            <div className="flex items-center gap-2">
              <User size={20} className="text-primary" />
              <h1 className="page-title">
                {contact.full_name}
              </h1>
            </div>
            {contact.title && (
              <p className="ml-7 text-sm text-muted-foreground">{contact.title}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {contact && id && (
            <QuickActions
              entity={{
                id,
                entityType: "Contact",
                name: contact.full_name,
                primaryEmail: contact.emails.find((e) => e.is_primary)?.email ?? contact.emails[0]?.email ?? null,
                primaryPhone: contact.phones.find((p) => p.is_primary)?.number ?? contact.phones[0]?.number ?? null,
              } satisfies QuickActionsEntity}
              onOpenNotes={handleOpenNotes}
            />
          )}
          <div className="mx-1 hidden md:block h-5 w-px bg-border" />
          <button onClick={() => navigate(`/contacts/${id}/edit`)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input px-3 text-sm text-foreground">
            <Pencil size={14} /> {t("common.edit")}
          </button>
          <button onClick={handleDelete}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm text-error">
            <Trash2 size={14} /> {t("common.delete")}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {/* Contact info */}
          <section className="surface-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Dane kontaktowe</h2>
            <div className="space-y-2">
              {contact.emails.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-sm">
                  <Mail size={14} className="text-muted-foreground" />
                  <a href={`mailto:${e.email}`} className="hover:underline text-primary">{e.email}</a>
                  {e.is_primary && <Badge variant="outline" className="text-[10px]">Glowny</Badge>}
                  {e.label && <span className="text-xs text-muted-foreground">({e.label})</span>}
                </div>
              ))}
              {contact.phones.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-muted-foreground" />
                  <a href={`tel:${p.number}`} className="hover:underline text-primary">{p.number}</a>
                  {p.is_primary && <Badge variant="outline" className="text-[10px]">Glowny</Badge>}
                  {p.label && <span className="text-xs text-muted-foreground">({p.label})</span>}
                </div>
              ))}
            </div>
          </section>

          {/* Companies */}
          {contact.companies.length > 0 && (
            <section className="surface-card p-4 space-y-3">
              <h2 className="text-sm font-semibold text-foreground">Powiazane firmy</h2>
              <div className="space-y-2">
                {contact.companies.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:underline text-foreground"
                    onClick={() => navigate(`/companies/${c.id}`)}>
                    <Building2 size={14} className="text-muted-foreground" />
                    <span>{c.name}</span>
                    {c.role && <span className="text-xs text-muted-foreground">({c.role})</span>}
                    {c.is_primary && <Badge variant="outline" className="text-[10px]">Glowny</Badge>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="surface-card p-4 space-y-3">
            {contact.department && (
              <div>
                <span className="text-xs text-muted-foreground">{t("contacts.department")}</span>
                <p className="text-sm text-foreground">{contact.department}</p>
              </div>
            )}
            {contact.owner && (
              <div>
                <span className="text-xs text-muted-foreground">Opiekun</span>
                <p className="text-sm text-foreground">{contact.owner.first_name} {contact.owner.last_name}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">RODO</span>
              <p className="text-sm text-foreground">
                {contact.gdpr_marketing_consent ? "Zgoda wyrazona" : "Brak zgody"}
                {contact.gdpr_consent_date && ` (${formatDate(contact.gdpr_consent_date)})`}
              </p>
            </div>
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(contact.created_at)}</p>
            </div>
            {contact.tags.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Tagi</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {contact.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Entity Tabs */}
      {id && (
        <div ref={tabsRef}>
          <EntityTabs
            entityType="Contact"
            entityId={id}
            tabs={CONTACT_TABS}
            defaultTab="notes"
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>
      )}
    </div>
  )
}
